/* eslint-disable no-template-curly-in-string */
import { useMutation } from "@apollo/client";
import { saveAs } from "file-saver";
import { useFormik } from "formik";
import { deburr } from "lodash";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { GET_IMAGE_URLS as getImageUrls } from "../../../graphql/mutation/Image";
import { zipAndDownloadFiles } from "../../../util/zipper";
import { GridInputs } from "../../Functional";
import { Button, Modal, Spinner } from "../../ui";
import Toggle from "../../ui/Input/components/Toggle";
import { getArtworkYear } from "./exportImageUtils";

const MAX_ZIP_BYTES = 2 * 1024 * 1024 * 1024; // 2GB

const getImageBlob = async ({ fileName, fileUrl, artworkId }) => {
  const file = await fetch(fileUrl);

  if (file.ok) {
    const blobFile = await file.blob();
    return new File([blobFile], fileName);
  } else {
    const err = new Error(file.statusText);
    err.artworkId = artworkId;
    err.status = file.status;

    throw err;
  }
};

const batchFilesBySize = (files, maxBytes) => {
  return files.reduce(
    (batches, file) => {
      const currentBatch = batches[batches.length - 1];
      const currentSize = currentBatch.reduce((sum, f) => sum + f.size, 0);

      if (currentBatch.length === 0 || currentSize + file.size <= maxBytes) {
        currentBatch.push(file);
      } else {
        batches.push([file]);
      }
      return batches;
    },
    [[]],
  );
};

const downloadAsZip = async (files, baseZipName) => {
  const batches = batchFilesBySize(files, MAX_ZIP_BYTES);
  await Promise.all(
    batches.map((batch, batchIndex) => {
      const suffix = batches.length > 1 ? ` (${batchIndex + 1})` : "";

      return zipAndDownloadFiles(batch, `${baseZipName}${suffix}.zip`);
    }),
  );
};

const downloadIndividually = files => files.forEach(file => saveAs(file));

const ExportImage = props => {
  const {
    currentSlide = 0,
    disabled = false,
    images,
    selectedImageFilters,
    allImageFilters,
  } = props;
  const image = images?.[currentSlide];
  const [loading, setLoading] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [useZip, setUseZip] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      exportQty: "selected",
      name: "${artist} - ${title} - ${year}",
      format: "tif",
      type: "t",
    },
  });
  const { exportQty, format, name, type } = formik.values;

  const [getImagesUrl] = useMutation(
    getImageUrls({
      artists: { id: true, name: true },
      editions: {},
      title: true,
      images: false,
      instanceReference: true,
    }),
    {
      variables: {
        input: {
          ...(exportQty === "all"
            ? {
                artworkIds: allImageFilters
                  ? allImageFilters?.artworkId
                  : [image?.artworkId],
              }
            : {
                artworkIds: selectedImageFilters
                  ? selectedImageFilters?.artworkId
                  : [image?.artworkId],
              }),
          ...(exportQty === "all"
            ? {
                archiveIds: allImageFilters ? allImageFilters?.archiveId : null,
              }
            : {
                archiveIds: selectedImageFilters
                  ? selectedImageFilters?.archiveId
                  : [image?.archiveId],
              }),
          format,
          type,
        },
      },
    },
  );

  const getFileName = ({ name, artwork }) => {
    const artistNames = artwork?.artists
      ?.map(artist => artist.name)
      ?.join(", ");
    const editions = artwork?.editions?.sort((a, b) => b.number - a.number); // descending

    // replace merge tags in name with data
    const data = {
      artist:
        artistNames?.length > 100
          ? artistNames?.substring(0, 100)?.trim()
          : artistNames?.trim() || "artist",
      title:
        artwork?.title?.length > 100
          ? artwork?.title?.substring(0, 100)?.trim()
          : artwork?.title?.trim() || "title",
      year: getArtworkYear(editions),
      reference: artwork?.instanceReference || "Instance_reference",
      artworkId: artwork?.id || "artworkId",
    };

    const fileName = name?.replace(/\${(.*?)}/g, (match, key) => {
      return deburr(data[key]);
    });

    return fileName;
  };

  const getFileNameAndFormat = ({ format, name, artwork }) => {
    const fileName = getFileName({ name, artwork });
    return `${fileName}.${format}`;
  };

  const handleExportImages = async ({ closeModal }) => {
    setLoading(true);
    setExportError(null);

    try {
      const response = await getImagesUrl();
      const urls = response?.data?.getImageUrls?.urls || [];
      const artworks = response?.data?.getImageUrls?.artworks || [];

      if (!response?.data?.getImageUrls?.success) {
        const [firstError] = response?.errors || [];
        throw firstError;
      }

      const files = await Promise.all(
        urls.map(async (url, index) => {
          const { href, artworkId } = url;
          const artwork = artworks.find(a => a.id === artworkId);

          return await getImageBlob({
            fileName: getFileNameAndFormat({
              format,
              name: `${name}_${index + 1}`,
              artwork,
            }),
            fileUrl: href,
            artworkId,
          });
        }),
      );

      if (useZip) {
        const downloadName =
          exportQty === "selected" ? "Selected List Images" : "Artwork Images";

        await downloadAsZip(files, downloadName);
      } else {
        downloadIndividually(files);
      }

      formik.resetForm();
      closeModal();
      setLoading(false);
    } catch (error) {
      let exportError = {
        msg: error.message,
      };

      if (error?.artworkId) {
        exportError = {
          msg: `Missing image. Please process the artwork images, and try again.`,
          artworkId: error.artworkId,
        };
      } else if (error?.name === "ApolloError") {
        // coming from resolver
        const regex = /artwork:\s*(\d+).*?format\s+(\w+)/;
        const match = regex.exec(error.message);
        const [_, artworkId, format] = match || [];

        if (artworkId && format) {
          exportError = {
            msg: `Missing image in ${format} format. Please process the artwork images, and try again.`,
            artworkId,
            format,
          };
        }
      } else if (error?.status) {
        // coming from network
        if (error.status === 404) {
          exportError = {
            msg: "Your selection contains missing images. Make sure all artworks are processed.",
          };
        }
      }

      setExportError(exportError);
      setLoading(false);
    }
  };

  const inputs = {
    className: "mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2",
    inputs: [
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          {
            label: "Export",
            name: "exportQty",
            type: "select",
            placeholder: "Drop Down",
            options: [
              { label: "Selected Image", value: "selected" },
              { label: "All", value: "all" },
            ],
            ...(selectedImageFilters || allImageFilters
              ? { disabled: true }
              : null),
          },
          {
            label: "File Name",
            name: "name",
            type: "select",
            placeholder: "Drop Down",
            options: [
              {
                label: "Artist, Title, Year",
                value: "${artist} - ${title} - ${year}",
              },
              { label: "Reference", value: "${reference}" },
              { label: "Artwork ID", value: "${artworkId}" },
            ],
          },
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          {
            label: "Format",
            name: "format",
            type: "select",
            placeholder: "Drop Down",
            options: [
              { label: "TIF", value: "tif" },
              { label: "PNG", value: "png" },
              { label: "JPEG", value: "jpeg" },
            ],
          },
          {
            label: "Size",
            name: "type",
            type: "select",
            placeholder: "Drop Down",
            options: [
              { label: "Thumbnail", value: "t" },
              { label: "Screen", value: "x" },
              { label: "Medium Res", value: "a" },
              { label: "Higher Res", value: "b" },
            ],
          },
        ],
      },
    ],
  };

  const detailInputProps = {
    formik,
    ...inputs,
  };

  const modalProps = {
    scale: "md",
    closeOnBackdrop: false,
    hideCloseButton: true,
    disabled,
    body: ({ closeModal }) => {
      return (
        <div className="relative flex w-full min-w-0 flex-col p-3 sm:p-4 lg:p-8">
          {loading && (
            <div className="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center bg-gray-200/50 backdrop-blur-sm">
              <Spinner />
            </div>
          )}
          <div className="flex flex-col gap-4 lg:flex-row">
            <div className="flex min-w-0 flex-1 items-center font-bold">
              <span className="text-2xl">Export Artwork Images</span>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <Toggle
                label=".zip"
                disabled={loading}
                onChange={setUseZip}
                value={useZip}
              />
              <Button
                action="default"
                className="lg:mr-5"
                label="Cancel"
                onClick={() => {
                  formik?.resetForm();
                  setExportError(null);
                  closeModal();
                }}
              />
              <Button
                disabled={loading}
                label="Export"
                loading={loading}
                onClick={() => {
                  handleExportImages({ closeModal });
                }}
              />
            </div>
          </div>
          {exportError && (
            <div className="mt-3 flex flex-wrap items-center justify-start rounded-md border border-transparent bg-red-100 px-4 py-2 font-bold font-medium text-red-800 sm:text-sm">
              {exportError?.msg ||
                "Error exporting image, please try again later."}
              {exportError?.artworkId && (
                <span>
                  <Button
                    label="Go to Artwork"
                    action="link"
                    className="mx-2"
                    onClick={() => {
                      closeModal();
                      navigate(
                        `/artworks/${exportError.artworkId}/details?status=all`,
                      );
                    }}
                  />
                </span>
              )}
            </div>
          )}
          <GridInputs {...detailInputProps} />
        </div>
      );
    },
  };

  return (
    <Modal {...modalProps}>
      <Button disabled={disabled} label="Export" className={"!w-32"} />
    </Modal>
  );
};

export default ExportImage;
